import { Mui } from '@osu/react-ui';
import { useState } from 'react';
import { skipToken } from "@reduxjs/toolkit/query";
import MenuDialogBase from "./MenuDialogBase";
import { useGetScholarsProgramsQuery, useAdminRecommendScholarMutation } from "../../../services/graphql/api";
import { getScholarsApplDetailParams } from "../ScholarsApplicant";

// Single-applicant administrative recommendation (Scholars applicant-detail Actions menu). ADMIN-only
// (gated by the menu item + the resolver's SCHOLARS-ADMIN group). Mirrors the Advanced Search bulk admin
// recommendation UX (DialogScholarsBulkRecommendation): the admin first picks the decision (Admit vs Deny),
// which reveals the fields that decision needs:
//   - ADMIT: a target sub-program (reason is the fixed "Admin Recommendation", stamped server-side). Routes
//     the applicant to Recommendations to Approve for final approval.
//   - DENY: one of the three fixed deny scenarios. Parks the applicant pending final-deny on Recommendations
//     to Approve.
// Unlike the bulk dialog this is scoped to the ONE applicant on the detail page and runs synchronously, so
// it reuses the shared MenuDialogBase shell (same as DialogAssignToProgram) rather than a bespoke dialog.
export const RECOMMENDATIONS = {
    Admit: "Admit",
    Deny: "Deny",
};

// Same fixed scenarios as the bulk deny (DialogScholarsBulkRecommendation.SCHOLARS_BULK_DENY_REASONS).
export const SCHOLARS_ADMIN_DENY_REASONS = [
    "Not invited to preference (too late / does not meet eligibility criteria)",
    "Invited to choose preferences but never submitted",
    "Preferenced and reviewed but not admitted to any program",
];

export default function DialogAdminRecommendation({ menuItemAndClose }) {
    const params = getScholarsApplDetailParams();

    const [recommendation, setRecommendation] = useState("");
    const [subProgramId, setSubProgramId] = useState("");
    const [selectedReason, setSelectedReason] = useState("");

    const [apiCall, result] = useAdminRecommendScholarMutation();

    const isAdmit = recommendation === RECOMMENDATIONS.Admit;
    const isDeny = recommendation === RECOMMENDATIONS.Deny;

    // Only load sub-programs once the admin has chosen Admit.
    const { data: programs = [], isFetching: programsLoading } = useGetScholarsProgramsQuery(
        isAdmit ? undefined : skipToken
    );

    // The single-applicant mutation: ADMIT sends subProgramId + decision ADMIT; DENY sends reason + decision DENY.
    const variables = isAdmit
        ? { applicantEmployeeId: params?.applicantEmployeeId, decision: "ADMIT", subProgramId }
        : { applicantEmployeeId: params?.applicantEmployeeId, decision: "DENY", reason: selectedReason };

    const isDisabled = !recommendation
        || (isAdmit && (programsLoading || !subProgramId))
        || (isDeny && !selectedReason);

    const buttons = [{ label: "Submit", apiCall, variables, isDisabled }];
    const dropdownsDisabled = result?.isLoading || result?.isSuccess;

    const message = !recommendation
        ? "Choose the administrative recommendation to post for this applicant."
        : isAdmit
            ? "This will ADMIT this applicant into the sub-program you choose. "
              + "The recommendation reason is recorded as \"Admin Recommendation\"."
            : "This will DENY this applicant. Choose the reason that applies. "
              + "Denied applicants are moved to Recommendations to Approve as a pending deny for final approval.";

    return (
        <MenuDialogBase menuItemAndClose={menuItemAndClose}
            apiCall={apiCall}
            buttons={buttons}
            result={result}
            extraContent={(
                <>
                    <Mui.Typography tabIndex={0} sx={{ marginBottom: "1rem" }}>{message}</Mui.Typography>

                    <Mui.FormControl fullWidth required>
                        <Mui.InputLabel id="scholars-adminrec-rec-label">Recommendation</Mui.InputLabel>
                        <Mui.Select
                            disabled={dropdownsDisabled}
                            labelId="scholars-adminrec-rec-label"
                            id="scholars-adminrec-rec-select"
                            label="Recommendation"
                            value={recommendation}
                            onChange={(e) => {
                                // Switching the decision clears the decision-specific field so a stale
                                // sub-program / reason can't be submitted for the wrong decision.
                                setRecommendation(e.target.value);
                                setSubProgramId("");
                                setSelectedReason("");
                            }}
                            sx={{ marginBottom: "1rem", width: "100%" }}
                        >
                            <Mui.MenuItem value=""> - Choose a Recommendation - </Mui.MenuItem>
                            <Mui.MenuItem value={RECOMMENDATIONS.Admit}>{RECOMMENDATIONS.Admit}</Mui.MenuItem>
                            <Mui.MenuItem value={RECOMMENDATIONS.Deny}>{RECOMMENDATIONS.Deny}</Mui.MenuItem>
                        </Mui.Select>
                    </Mui.FormControl>

                    {isAdmit && (programsLoading ? (
                        <Mui.Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
                            <Mui.CircularProgress />
                        </Mui.Box>
                    ) : (
                        <Mui.FormControl fullWidth required>
                            <Mui.InputLabel id="scholars-adminrec-program-label">Sub-Program</Mui.InputLabel>
                            <Mui.Select
                                disabled={dropdownsDisabled}
                                labelId="scholars-adminrec-program-label"
                                id="scholars-adminrec-program-select"
                                label="Sub-Program"
                                value={subProgramId}
                                onChange={(e) => setSubProgramId(e.target.value)}
                                sx={{ width: "100%" }}
                            >
                                <Mui.MenuItem value=""> - Choose a Sub-Program - </Mui.MenuItem>
                                {(programs || []).map((program) => (
                                    <Mui.MenuItem key={program.subProgramId} value={program.subProgramId} disabled={program.isOpen === false}>
                                        {program.label || program.subProgramId}{program.isOpen === false ? " (Closed)" : ""}
                                    </Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                    ))}

                    {isDeny && (
                        <Mui.FormControl fullWidth required>
                            <Mui.InputLabel id="scholars-adminrec-reason-label">Reason</Mui.InputLabel>
                            <Mui.Select
                                disabled={dropdownsDisabled}
                                labelId="scholars-adminrec-reason-label"
                                id="scholars-adminrec-reason-select"
                                label="Reason"
                                value={selectedReason}
                                onChange={(e) => setSelectedReason(e.target.value)}
                                sx={{ width: "100%" }}
                            >
                                <Mui.MenuItem value=""> - Choose a Reason - </Mui.MenuItem>
                                {SCHOLARS_ADMIN_DENY_REASONS.map((option) => (
                                    <Mui.MenuItem key={option} value={option}>{option}</Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                    )}
                </>
            )}
        />
    );
}
